// Premium.jsx — página de pago web (Stripe Checkout)
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import Header from "../components/Header";
import Footer from "../components/Footer";
import api from "../api/axios";
import { trackBeginCheckout, getGaClientId } from "../analytics/googleAnalytics";
import { PRECIO_ANUAL, PRECIO_ANUAL_ISO, PRECIO_MENSUAL_EQUIV, MONEDA, DIAS_PRUEBA } from "../config/precios";
import { BENEFICIOS, COMO_FUNCIONA, INCLUYE, FAQS } from "../data/premiumContenido";
import "./Premium.css";

export default function Premium() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleCheckout = async () => {
    setLoading(true);
    setError("");
    trackBeginCheckout();
    try {
      // Pasamos el client_id de GA para que el backend pueda atribuir el cobro
      // real (invoice.paid) al mismo usuario/canal vía Measurement Protocol.
      const gaClientId = getGaClientId();
      const { data } = await api.post("/checkout/stripe", gaClientId ? { ga_client_id: gaClientId } : {});
      if (data?.url) {
        window.location.href = data.url;
      } else {
        setError("No se pudo iniciar el pago. Inténtalo de nuevo.");
        setLoading(false);
      }
    } catch (e) {
      setError("No se pudo iniciar el pago. Inténtalo de nuevo en unos minutos.");
      setLoading(false);
    }
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map(([q, a]) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };

  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Wayo Premium",
    description:
      "Nutrición y entrenamiento personalizados con Wayo: escaneos de comida ilimitados, plan a medida, rutinas y +100 recetas. 7 días gratis.",
    brand: { "@type": "Brand", name: "Wayo" },
    offers: {
      "@type": "Offer",
      price: PRECIO_ANUAL_ISO,
      priceCurrency: MONEDA,
      availability: "https://schema.org/InStock",
      url: "https://wayoai.app/premium",
    },
  };

  return (
    <>
      <Helmet>
        <title>Wayo Premium · Nutrición y entrenamiento personalizados | 7 días gratis</title>
        <meta
          name="description"
          content="Hazte Wayo Premium: escaneos de comida ilimitados, plan de alimentación y rutinas personalizadas y +100 recetas. 49,99 €/año con 7 días gratis. Cancela cuando quieras."
        />
        <link rel="canonical" href="https://wayoai.app/premium" />
        <meta property="og:title" content="Wayo Premium · 7 días gratis" />
        <meta
          property="og:description"
          content="Nutrición y entrenamiento personalizados sin contar calorías a mano. 49,99 €/año, 7 días gratis."
        />
        <meta property="og:url" content="https://wayoai.app/premium" />
        <meta property="og:type" content="product" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(productJsonLd)}</script>
      </Helmet>

      <Header />

      <main className="premium-web">
        <section className="premium-web-hero">
          <span className="premium-web-badge">Wayo Premium</span>
          <h1 className="premium-web-title">
            Todo lo que necesitas, <span>en una sola app</span>
          </h1>
          <p className="premium-web-subtitle">
            Nutrición y entrenamiento personalizados, sin contar calorías ni
            saltar entre apps. Empieza con 7 días gratis.
          </p>

          <div className="premium-web-card">
            <div className="premium-web-price">
              <span className="premium-web-price-amount">{PRECIO_ANUAL}</span>
              <span className="premium-web-price-period">/año</span>
            </div>
            <p className="premium-web-price-note">
              Equivale a 4,17 €/mes · 7 días gratis · Cancela cuando quieras
            </p>

            <ul className="premium-web-benefits">
              {BENEFICIOS.map((b) => (
                <li key={b}>
                  <span className="premium-web-check">✓</span> {b}
                </li>
              ))}
            </ul>

            <button
              className="premium-web-cta"
              onClick={handleCheckout}
              disabled={loading}
            >
              {loading ? "Redirigiendo a pago seguro…" : "Empezar 7 días gratis"}
            </button>

            {error && <p className="premium-web-error">{error}</p>}

            <p className="premium-web-secure">
              🔒 Pago seguro con Stripe · Tras pagar recibirás un email para
              activar tu cuenta
            </p>
          </div>

          <p className="premium-web-alt">
            ¿Prefieres probar gratis primero?{" "}
            <a
              href="https://apps.apple.com/app/id6756500478"
              target="_blank"
              rel="noopener noreferrer"
            >
              Descarga la app
            </a>{" "}
            y úsala con el plan gratuito.
          </p>
        </section>

        {/* Cómo funciona */}
        <section className="premium-web-section">
          <h2 className="premium-web-h2">Cómo funciona</h2>
          <div className="premium-web-steps">
            {COMO_FUNCIONA.map((p, i) => (
              <div className="premium-web-step" key={p.titulo}>
                <div className="premium-web-step-icon">{p.icon}</div>
                <div className="premium-web-step-num">Paso {i + 1}</div>
                <h3 className="premium-web-step-title">{p.titulo}</h3>
                <p className="premium-web-step-desc">{p.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Qué incluye */}
        <section className="premium-web-section">
          <h2 className="premium-web-h2">Qué incluye Premium</h2>
          <div className="premium-web-includes">
            {INCLUYE.map(([titulo, desc]) => (
              <div className="premium-web-include" key={titulo}>
                <span className="premium-web-check">✓</span>
                <div>
                  <strong>{titulo}</strong>
                  <span>{desc}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="premium-web-section">
          <h2 className="premium-web-h2">Preguntas frecuentes</h2>
          <div className="premium-web-faq">
            {FAQS.map(([q, a]) => (
              <details className="premium-web-faq-item" key={q}>
                <summary>{q}</summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA final */}
        <section className="premium-web-final">
          <h2 className="premium-web-final-title">Empieza tus 7 días gratis</h2>
          <p className="premium-web-final-sub">
            Sin pagar nada hoy. Cancela cuando quieras.
          </p>
          <button
            className="premium-web-cta premium-web-cta-final"
            onClick={handleCheckout}
            disabled={loading}
          >
            {loading ? "Redirigiendo a pago seguro…" : "Empezar 7 días gratis"}
          </button>
        </section>
      </main>

      <Footer />
    </>
  );
}
